import { RefObject, UIEvent, useCallback, useEffect, useRef } from 'react';

interface UseScrollFallbackWhenNotOverflowingParams {
  containerRef: RefObject<HTMLElement | null>;
  onScroll?: (e: UIEvent) => unknown;
  /**
   * any value that changes when the list re-renders with new data (typically
   * `records.length`). Used to re-check overflow after each page arrives.
   */
  dataKey: number;
}

/**
 * Backstop for scroll-driven pagination on containers that may not overflow.
 *
 * On large screens or zoomed-out viewports, the first page of records can fit
 * entirely inside the scroll container — leaving the user with nothing to
 * scroll and pagination frozen. This hook watches the container and forwards
 * a synthetic scroll event whenever `scrollHeight <= clientHeight`, so the
 * existing scroll handler can fetch the next page until overflow is restored.
 */
export const useScrollFallbackWhenNotOverflowing = ({
  containerRef,
  onScroll,
  dataKey,
}: UseScrollFallbackWhenNotOverflowingParams) => {
  const pendingRafRef = useRef<number | null>(null);

  const fireScrollWhenNotOverflowing = useCallback(() => {
    if (!onScroll || !containerRef.current) {
      return;
    }

    // skip while the first page is still loading. to avoid producing duplicate rows on merge.
    if (!dataKey) {
      return;
    }

    if (containerRef.current.scrollHeight > containerRef.current.clientHeight) {
      return;
    }

    onScroll({
      target: containerRef.current,
      currentTarget: containerRef.current,
      // overriding native event with custom object data
    } as unknown as UIEvent);
  }, [onScroll, containerRef, dataKey]);

  useEffect(() => {
    if (!onScroll) {
      return;
    }

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(fireScrollWhenNotOverflowing);
      pendingRafRef.current = raf2;
    });

    pendingRafRef.current = raf1;

    return () => {
      if (pendingRafRef.current !== null) {
        cancelAnimationFrame(pendingRafRef.current);
      }
    };
  }, [dataKey, onScroll, fireScrollWhenNotOverflowing]);

  // window resize, browser zoom, or panel resize can also flip the container
  // from overflowing to fits within viewport, so re-check on resize.
  useEffect(() => {
    if (!onScroll || !containerRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const containerObserver = new ResizeObserver(() => fireScrollWhenNotOverflowing());

    containerObserver.observe(containerRef.current);

    return () => containerObserver.disconnect();
  }, [onScroll, fireScrollWhenNotOverflowing, containerRef]);
};
